/**
 * The docked call panel can be moved (TRUS-668): drag it by its header (mouse,
 * pen or touch — pointer events), or Alt + arrow keys while it has focus. It
 * stays inside the window (re-clamped when the window resizes) and the spot is
 * remembered per browser. "Reset position" (call settings) puts it back.
 *
 * Only the wrapper's left / top change: the panel itself is never moved to
 * another container, so it never remounts (a remount would leave the call).
 * Buttons inside the header still click — a drag starts only on the header's
 * empty space, and only after the pointer has moved a few pixels.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type React from 'react';

export const PANEL_POS_KEY = 'weavein.calls.panelPos.v1';
export const PANEL_MARGIN = 8;
export const PANEL_KEY_STEP = 16;
const DRAG_THRESHOLD = 4;
export const DRAG_HANDLE_ATTR = 'data-call-drag-handle';
const INTERACTIVE = 'button, a, input, select, textarea, label, [role="button"], [role="menuitem"], [data-no-drag]';

export interface PanelPos {
  x: number;
  y: number;
}

/** Keep a panel of `size` fully inside the viewport (or pinned top-left if it cannot fit). */
export function clampPanelPos(
  pos: PanelPos,
  size: { width: number; height: number },
  viewport: { width: number; height: number },
  margin = PANEL_MARGIN,
): PanelPos {
  const maxX = Math.max(margin, viewport.width - size.width - margin);
  const maxY = Math.max(margin, viewport.height - size.height - margin);
  return {
    x: Math.round(Math.min(Math.max(pos.x, margin), maxX)),
    y: Math.round(Math.min(Math.max(pos.y, margin), maxY)),
  };
}

export function loadPanelPos(): PanelPos | null {
  try {
    const raw = window.localStorage.getItem(PANEL_POS_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    return v && Number.isFinite(v.x) && Number.isFinite(v.y) ? { x: Number(v.x), y: Number(v.y) } : null;
  } catch {
    return null;
  }
}

export function savePanelPos(pos: PanelPos | null): void {
  try {
    if (pos) window.localStorage.setItem(PANEL_POS_KEY, JSON.stringify(pos));
    else window.localStorage.removeItem(PANEL_POS_KEY);
  } catch {
    /* remembered for this page only */
  }
}

export interface CallPanelPosition {
  /** The panel was moved from its default corner. */
  moved: boolean;
  reset: () => void;
}

/** CallsHost provides it for the docked panel; the settings sheet offers "Reset position". */
export const CallPanelPositionContext = createContext<CallPanelPosition | null>(null);
export const useCallPanelPosition = () => useContext(CallPanelPositionContext);

interface DragState {
  pointerId: number;
  startX: number;
  startY: number;
  origX: number;
  origY: number;
  moved: boolean;
  handle: Element;
}

export function useDraggablePanel(enabled: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPosState] = useState<PanelPos | null>(() => loadPanelPos());
  const posRef = useRef(pos);
  const drag = useRef<DragState | null>(null);

  const setPos = useCallback((next: PanelPos | null) => {
    posRef.current = next;
    setPosState(next);
  }, []);

  const clampNow = useCallback((p: PanelPos): PanelPos => {
    const el = ref.current;
    const r = el?.getBoundingClientRect();
    return clampPanelPos(
      p,
      { width: r?.width ?? 0, height: r?.height ?? 0 },
      { width: window.innerWidth, height: window.innerHeight },
    );
  }, []);

  // Re-clamp on resize (and once the panel has a size).
  const hasPos = pos != null;
  useEffect(() => {
    if (!enabled || !hasPos) return;
    const onResize = () => {
      const cur = posRef.current;
      if (!cur) return;
      const next = clampNow(cur);
      if (next.x !== cur.x || next.y !== cur.y) setPos(next);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [enabled, hasPos, clampNow, setPos]);

  // Move / up are followed on the window (and the pointer is captured by the
  // header): a fast drag leaves the panel at once, and the wrapper itself is
  // pointer-events: none so the page under it stays usable.
  const onMove = useCallback(
    (e: PointerEvent) => {
      const d = drag.current;
      if (!d || e.pointerId !== d.pointerId) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      if (!d.moved) {
        if (Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;
        d.moved = true;
      }
      e.preventDefault();
      setPos(clampNow({ x: d.origX + dx, y: d.origY + dy }));
    },
    [clampNow, setPos],
  );

  const endDrag = useRef<(e: PointerEvent) => void>(() => {});
  const onUp = useCallback((e: PointerEvent) => endDrag.current(e), []);
  const stopListening = useCallback(() => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('pointercancel', onUp);
  }, [onMove, onUp]);
  endDrag.current = (e: PointerEvent) => {
    const d = drag.current;
    if (!d || e.pointerId !== d.pointerId) return;
    drag.current = null;
    stopListening();
    try {
      (d.handle as HTMLElement).releasePointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
    if (d.moved) savePanelPos(posRef.current);
  };
  useEffect(() => stopListening, [stopListening]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!enabled || (e.pointerType === 'mouse' && e.button !== 0)) return;
      const target = e.target as Element | null;
      const handle = target?.closest?.(`[${DRAG_HANDLE_ATTR}]`);
      if (!handle || target?.closest?.(INTERACTIVE)) return;
      const rect = ref.current?.getBoundingClientRect();
      if (!rect) return;
      drag.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        origX: rect.left,
        origY: rect.top,
        moved: false,
        handle,
      };
      try {
        // Keeps the moves coming over iframes (tabbed windows) and outside the window.
        (handle as HTMLElement).setPointerCapture?.(e.pointerId);
      } catch {
        /* jsdom / old browsers */
      }
      stopListening();
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [enabled, onMove, onUp, stopListening],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!enabled || !e.altKey) return;
      const step: Record<string, [number, number]> = {
        ArrowLeft: [-PANEL_KEY_STEP, 0],
        ArrowRight: [PANEL_KEY_STEP, 0],
        ArrowUp: [0, -PANEL_KEY_STEP],
        ArrowDown: [0, PANEL_KEY_STEP],
      };
      const delta = step[e.key];
      if (!delta) return;
      e.preventDefault();
      const rect = ref.current?.getBoundingClientRect();
      const base = posRef.current ?? { x: rect?.left ?? 0, y: rect?.top ?? 0 };
      const next = clampNow({ x: base.x + delta[0], y: base.y + delta[1] });
      setPos(next);
      savePanelPos(next);
    },
    [enabled, clampNow, setPos],
  );

  const reset = useCallback(() => {
    setPos(null);
    savePanelPos(null);
  }, [setPos]);

  return {
    ref,
    pos: enabled ? pos : null,
    moved: enabled && pos != null,
    reset,
    handlers: { onPointerDown, onKeyDown },
  };
}
